import { t } from "@lingui/macro";
import { invariantResponse } from "@winamp/invariant";
import { jsonWithError, jsonWithSuccess } from "@winamp/toast/index.js";
import { parseWithValibot } from "conform-to-valibot";
import type { ActionFunctionArgs } from "react-router";
import * as v from "valibot";
import { requireAuth } from "~/modules/auth/auth.server";
import { OpenAPI, getResponseErrors } from "~/modules/sdk/client.server";

export const getArtistLinkSchema = () =>
  v.object({
    label: v.optional(v.nullable(v.string(), null), null),
    url: v.pipe(
      v.string(t`The URL is required.`),
      v.url(t`The URL should be a valid URL.`),
    ),
  });

export async function action({ request, params }: ActionFunctionArgs) {
  const { accessToken } = await requireAuth(request, {
    throwOnRefresh: true,
  });

  invariantResponse(params.artistId, "An artist must be selected.", {
    status: 400,
  });
  invariantResponse(params.linkId, "A link ID must be selected.", {
    status: 400,
  });

  const formData = await request.formData();

  switch (formData.get("action")) {
    case "delete": {
      const { error } = await OpenAPI.DELETE(
        "/api/artists/{artistUid}/links/{linkUid}",
        {
          params: {
            path: { artistUid: params.artistId, linkUid: params.linkId },
          },
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      if (error) {
        return jsonWithError(
          {
            status: "error" as const,
            message: error.message,
          },
          {
            title: t`Deletion failed!`,
            message: t`The link could not be deleted.`,
          },
          { status: error.status },
        );
      }

      return jsonWithSuccess(
        { status: "success" as const },
        {
          title: t`Link deleted!`,
          message: t`The link has been deleted successfully.`,
        },
      );
    }
    default: {
      const ArtistLinkSchema = getArtistLinkSchema();
      const submission = parseWithValibot(formData, {
        schema: ArtistLinkSchema,
      });

      if (submission.status !== "success") {
        return submission.reply({
          resetForm: false,
          formErrors: ["The links page could not be saved."],
        });
      }

      const { error } = await OpenAPI.PUT(
        "/api/artists/{artistUid}/links/{linkUid}",
        {
          params: {
            path: { artistUid: params.artistId, linkUid: params.linkId },
          },
          headers: { Authorization: `Bearer ${accessToken}` },
          body: submission.value,
        },
      );

      if (error) {
        if (error) {
          const { fieldErrors, formErrors } = getResponseErrors(error);

          return submission.reply({
            resetForm: false,
            fieldErrors,
            formErrors: fieldErrors
              ? formErrors
              : (formErrors ?? [t`Editing the link did not work. Try Again.`]),
          });
        }
      }

      return jsonWithSuccess(
        { status: "success" as const },
        {
          title: t`Link updated!`,
          message: t`The link has been updated successfully.`,
        },
      );
    }
  }
}
